<template>
    <MeasurementItem>
        <template #content>
            <div v-if="isLoading" class="no-padding l-full-width">
                <VSpinner size="medium" line-fg-color="black" :speed="1" />
            </div>

            <div v-else class="l-full-width">
                <ApexChart
                    :options="chartOptions"
                    :series="series"
                    :height="chartHeight"
                />
            </div>
        </template>
    </MeasurementItem>
</template>

<script>
import moment from 'moment'
import ApexChart from 'vue-apexcharts'
import VSpinner from 'vue-simple-spinner'

import { formatHelper, httpHelper } from '../utils'
import MeasurementItem from './MeasurementItem'
import MeasurementListHelper from '../mixins/MeasurementListHelper'

const activitiesToDisplay = [3, 2, 8, 0]

export default {
    name: 'TruckTimelineMeasurementFragment',
    components: {
        ApexChart,
        MeasurementItem,
        VSpinner,
    },
    mixins: [MeasurementListHelper],
    props: {
        selectedDate: {
            type: String,
            default: () => moment().format('YYYY-MM-DD'),
            validator: value => {
                return moment(value, 'YYYY-MM-DD', true).isValid()
            },
        },
        hasTitle: {
            type: Boolean,
            default: true,
        },
        driver: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            activityTranslations: activitiesToDisplay.reduce(
                (acc, cur) => ({
                    ...acc,
                    [cur]: this.$t(`shared.tachograph.working_state.${cur}`),
                }),
                {}
            ),
            chartOptions: {
                title: {
                    text: this.hasTitle
                        ? this.driver?.name ||
                          this.sensorData?.driver_name?.value
                        : '',
                    align: 'center',
                },
                chart: {
                    type: 'rangeBar',
                    zoom: {
                        enabled: false,
                    },
                    toolbar: {
                        show: false,
                    },
                },
                legend: {
                    showForSingleSeries: true,
                },
                plotOptions: {
                    bar: {
                        horizontal: true,
                        barHeight: '50%',
                        rangeBarGroupRows: true,
                    },
                },
                colors: ['#d32f2f', '#00a1de', '#fdae61', '#41b883'],
                xaxis: {
                    type: 'datetime',
                    min: moment(this.selectedDate)
                        .startOf('day')
                        .valueOf(),
                    max: moment(this.selectedDate)
                        .add(1, 'day')
                        .startOf('day')
                        .valueOf(),
                    labels: {
                        datetimeUTC: false,
                    },
                },
                yaxis: {
                    showAlways: true,
                    showForNullSeries: false,
                    labels: {
                        formatter: value => {
                            return typeof value === 'string' ? value : ''
                        },
                    },
                },
                tooltip: {
                    x: {
                        formatter(value, data) {
                            if (typeof value === 'string') {
                                const { y: timespan } = data.w.config.series[
                                    data.seriesIndex
                                ].data[data.dataPointIndex]
                                return `${value} (${formatHelper.hoursAndMinutesDuration(
                                    (timespan[1] - timespan[0]) / 1000
                                )})`
                            }
                            return moment(value).format('HH:mm')
                        },
                    },
                },
            },
            isLoading: true,
            series: [],
        }
    },
    computed: {
        chartHeight() {
            let xFirst
            const isMultiSeries = !this.series.every(({ data }) =>
                data.every(({ x }) => {
                    if (x && !xFirst) {
                        xFirst = x
                    }
                    return x === xFirst
                })
            )
            return isMultiSeries ? 200 : 140
        },
    },
    watch: {
        'sensorData.driver_id': {
            immediate: true,
            async handler(newDriverId, oldDriverId) {
                if (newDriverId?.value === oldDriverId?.value) {
                    return
                }

                if (this.sensorData?.driver_name?.value && this.hasTitle) {
                    this.chartOptions.title.text = this.sensorData?.driver_name?.value
                }

                await this.reloadData(newDriverId?.value)
            },
        },
        driver: {
            immediate: true,
            async handler(newDriver, oldDriver) {
                if (newDriver?.driver_id === oldDriver?.driver_id) {
                    return
                }

                if (newDriver?.name && this.hasTitle) {
                    this.chartOptions.title.text = newDriver.name
                }

                await this.reloadData(newDriver?.driver_id)
            },
        },
        selectedDate: {
            immediate: true,
            handler() {
                const driverId =
                    this.driver?.driver_id || this.sensorData?.driver_id?.value
                if (driverId) {
                    this.reloadData(driverId)
                }
            },
        },
    },
    methods: {
        async reloadData(driverId) {
            try {
                this.isLoading = true
                const { data } = await httpHelper.getRecursively(
                    '/tachograph/driver-activities/',
                    {
                        params: {
                            driver_id: driverId,
                            start: moment(this.selectedDate)
                                .subtract(1, 'day')
                                .startOf('day')
                                .format(),
                            end: moment(this.selectedDate)
                                .endOf('day')
                                .format(),
                        },
                    }
                )

                const activities = data.results.reduce((acc, cur) => {
                    const startDay = cur.start.slice(0, 10)
                    const endDay = cur.end.slice(0, 10)
                    return startDay === endDay
                        ? [...acc, cur]
                        : [
                              ...acc,
                              {
                                  ...cur,
                                  end: `${startDay}T23:59:59${cur.start.slice(
                                      19
                                  )}`,
                              },
                              {
                                  ...cur,
                                  start: `${endDay}T00:00:00${cur.end.slice(
                                      19
                                  )}`,
                              },
                          ]
                }, [])

                this.series = activitiesToDisplay.map(activity => {
                    const data = activities.filter(
                        ({ activity_number }) => activity_number === activity
                    )
                    const duration = formatHelper.hoursAndMinutesDuration(
                        data.reduce(
                            (acc, cur) =>
                                acc +
                                (cur.start > this.selectedDate
                                    ? new Date(cur.end).getTime() -
                                      new Date(cur.start).getTime()
                                    : 0),
                            0
                        ) / 1000
                    )
                    return {
                        name: `${this.activityTranslations[activity] ||
                            activity} (${duration})`,
                        data: data.map(({ start, end }) => ({
                            x:
                                this.selectedDate ===
                                moment().format('YYYY-MM-DD')
                                    ? start > this.selectedDate
                                        ? this.$t('today')
                                        : this.$t('yesterday')
                                    : moment(start).format('LL'),
                            y: [
                                new Date(
                                    this.selectedDate + start.slice(10)
                                ).getTime(),
                                new Date(
                                    this.selectedDate + end.slice(10)
                                ).getTime(),
                            ],
                        })),
                    }
                })

                this.chartOptions.xaxis = {
                    ...this.chartOptions.xaxis,
                    min: moment(this.selectedDate)
                        .startOf('day')
                        .valueOf(),
                    max: moment(this.selectedDate)
                        .add(1, 'day')
                        .startOf('day')
                        .valueOf(),
                }
            } finally {
                this.isLoading = false
            }
        },
    },
}
</script>

<i18n>
{
    "en": {
        "today": "Today",
        "yesterday": "Yesterday"
    },
    "de": {
        "today": "Heute",
        "yesterday": "Gestern"
    },
    "fr": {
        "today": "Aujourd'hui",
        "yesterday": "Hier"
    },
    "it": {
        "today": "Oggi",
        "yesterday": "Ieri"
    }
}
</i18n>
